export type AuthModalStatus = "initial" | "sending" | "error" | "confirmation";

export type AuthModalConfig = {
  email?: string;
  tagline?: string;
  description?: string;
  /** Which state the modal opens into. Defaults to "initial". Use "sending" to fire the send
   *  immediately on open, e.g. for a trigger that already has a confirmed email and shouldn't
   *  need a second click inside the modal. */
  initialStatus?: AuthModalStatus;
  /** Called once a sign-in link has been sent successfully (fires once per open, even across
   *  resends), so the caller can react - e.g. hide a sticky bar elsewhere on the page. */
  onSent?: () => void;
  /** Overrides where a clicked sign-in link lands after auth completes (see Login's own
   *  redirectPath prop, which this is passed straight through to). Leave unset for the default
   *  (/dashboard, or whatever ?redirect= is on the current page). */
  redirectPath?: string;
};

type AuthModalState = {
  isOpen: boolean;
  config: AuthModalConfig;
  version: number;
  /** Email a link was last sent to, persisted independently of isOpen/version so the "Check
   *  Your Email" confirmation survives closing and reopening the modal - only cleared when the
   *  visitor explicitly asks to resend (see clearAuthModalLinkSent). */
  sentEmail: string | null;
};

const DEFAULT_AUTH_MODAL_CONFIG: AuthModalConfig = {
  email: "",
  tagline: "Sign in to your account",
  initialStatus: "initial",
};

let authModalState: AuthModalState = {
  isOpen: false,
  config: DEFAULT_AUTH_MODAL_CONFIG,
  version: 0,
  sentEmail: null,
};

const authModalListeners = new Set<() => void>();

export function getAuthModalState() {
  return authModalState;
}

export function subscribeToAuthModalState(listener: () => void) {
  authModalListeners.add(listener);

  return () => {
    authModalListeners.delete(listener);
  };
}

function notifyAuthModalListeners() {
  authModalListeners.forEach((listener) => listener());
}

export function openAuthModal(config?: AuthModalConfig) {
  authModalState = {
    ...authModalState,
    isOpen: true,
    config: { ...DEFAULT_AUTH_MODAL_CONFIG, ...config },
    version: authModalState.version + 1,
  };
  notifyAuthModalListeners();
}

export function closeAuthModal() {
  authModalState = {
    ...authModalState,
    isOpen: false,
  };
  notifyAuthModalListeners();
}

/** Marks that a sign-in link was successfully sent, so LoginModal shows the confirmation
 *  screen instead of the form the next time it's opened, until the visitor resends. */
export function markAuthModalLinkSent(email: string) {
  authModalState = {
    ...authModalState,
    sentEmail: email,
  };
  notifyAuthModalListeners();
}

/** Called from the confirmation screen's "Resend" link - takes the modal back to a clean form.
 *  Also resets initialStatus to "initial": leaving it as "sending" (from whichever trigger
 *  originally opened the modal) would otherwise auto-fire another send the instant <Login />
 *  remounts, with no chance for the visitor to review/change the email first. */
export function clearAuthModalLinkSent() {
  authModalState = {
    ...authModalState,
    sentEmail: null,
    config: {
      ...authModalState.config,
      initialStatus: "initial",
    },
  };
  notifyAuthModalListeners();
}
